import React, { Fragment, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
// lodash
import cloneDeep from 'lodash/cloneDeep';
// modals
import WriteNoteModal from './modals/WriteNoteModal';
import ChangeStatusModal from './modals/ChangeStatusModal';
import CardMessageModal from './modals/CardMessageModal';
import EditAddressModal from '../viewOrder/modals/EditAddressModal';
import ActionBoxModal from './modals/ActionBoxModal';
import CartModal from './modals/CartModal';
import NoteForTeamModal from './modals/NoteForTeamModal';
// actions
import {
  filterQuickActions,
  setOrderActionsData,
  setRequiredParamsForQuickActionsTable,
  setCurrentQuickActionsTablePage,
  setPremiumTableData,
  getZones,
} from 'store/orderQuickActions/dataTable/DataTableAction';
// selectors
import {
  getTimeSlotData,
  getUserAgentData,
  getFilterAreas,
  getIsNoteModalOpen,
  getIsChangeStatusModalOpen,
  getOrderStatus,
  getIsCardModalOpen,
  getIsAddressModalOpen,
  getActionAddressData,
  getOrderId,
  getIsActionBoxModalOpen,
  getIsCartModalOpen,
  getOrderRowNote,
  getIsNoteForTeamModalOpen,
  getTableData,
  getZonesFilterData,
} from 'store/orderQuickActions/dataTable/DataTableSelector';
// constants
import { filteInputs, initialPayload } from 'constant/filteInputs';
import { convertQueryParamsIntoObject } from 'constant/Helpers';
// components
import FilterComponent from 'components/shared/FilterComponent';
import PremiumQuickActionsDataTable from './PremiumQuickActionsDataTable';
import TableActions from './TableActions';
import PageTitle from 'components/shared/PageTitle';
import AppErrorBoundary from 'components/errorBoundary/AppErrorBoundary';

const PremiumOrdersQuickActionsPage = () => {
  const timeSlots = useSelector((state) => getTimeSlotData({ state }));
  const userAgents = useSelector((state) => getUserAgentData({ state }));
  const areas = useSelector((state) => getFilterAreas({ state }));
  const isNoteModalOpen = useSelector((state) => getIsNoteModalOpen({ state }));
  const isNoteForTeamModalOpen = useSelector((state) => getIsNoteForTeamModalOpen({ state }));
  const isChangeStatusModalOpen = useSelector((state) => getIsChangeStatusModalOpen({ state }));
  const isCardModalOpen = useSelector((state) => getIsCardModalOpen({ state }));
  const isAddressModalOpen = useSelector((state) => getIsAddressModalOpen({ state }));
  const isActionBoxModalOpen = useSelector((state) => getIsActionBoxModalOpen({ state }));
  const isCartModalOpen = useSelector((state) => getIsCartModalOpen({ state }));
  const addressDetails = useSelector((state) => getActionAddressData({ state }));
  const orderId = useSelector((state) => getOrderId({ state }));
  const orderNote = useSelector((state) => getOrderRowNote({ state }));
  const orderStatus = useSelector((state) => getOrderStatus({ state }));
  const data = useSelector((state) => getTableData({ state }));
  const zones = useSelector((state) => getZonesFilterData({ state }));
  const dispatch = useDispatch();
  const location = useLocation();

  const [inputValues, setInputValues] = useState(initialPayload);

  useEffect(() => {
    dispatch(getZones());
    const clonedInputValues = cloneDeep({ ...inputValues, line: 1 });
    setInputValues(clonedInputValues);

    if (location.search) {
      const queryObj = convertQueryParamsIntoObject(location.search);
      dispatch(setCurrentQuickActionsTablePage(parseInt(queryObj.page, 10)));
      dispatch(setRequiredParamsForQuickActionsTable(queryObj));
      dispatch(setPremiumTableData(queryObj.searchkey));
    } else {
      if (!data.data) {
        dispatch(setRequiredParamsForQuickActionsTable(clonedInputValues));
        dispatch(setPremiumTableData(clonedInputValues.searchkey));
      }
    }
    dispatch(setOrderActionsData());
  }, []);

  const handleSubmit = async () => {
    const updatedParams = { ...inputValues, orderstatus: orderStatus };

    dispatch(filterQuickActions(updatedParams, true, 'premium'));
  };

  return (
    <Fragment>
      <PageTitle title="Premium Orders" />
      {isChangeStatusModalOpen && (
        <ChangeStatusModal order={data?.data?.find((order) => order.id === orderId)} />
      )}
      {isNoteModalOpen && (
        <AppErrorBoundary>
          <WriteNoteModal isAction={true} orderNote={orderNote} premium={true} />
        </AppErrorBoundary>
      )}
      {isNoteForTeamModalOpen && (
        <AppErrorBoundary>
          <NoteForTeamModal />
        </AppErrorBoundary>
      )}
      {isCardModalOpen && (
        <AppErrorBoundary>
          <CardMessageModal />
        </AppErrorBoundary>
      )}
      {isAddressModalOpen && (
        <AppErrorBoundary>
          <EditAddressModal
            recipient={addressDetails.recipient}
            orderId={addressDetails.orderId}
            defaultLat={addressDetails.opsInfo.lat}
            defaultLng={addressDetails.opsInfo.lng}
            isAction={addressDetails.isAction}
            orderOps={addressDetails.orderOps}
            premium={true}
          />
        </AppErrorBoundary>
      )}
      {isActionBoxModalOpen && (
        <AppErrorBoundary>
          <ActionBoxModal orderId={orderId} isActionBoxModalOpen={isActionBoxModalOpen} />
        </AppErrorBoundary>
      )}
      {isCartModalOpen && (
        <AppErrorBoundary>
          <CartModal isAction={true} orderId={orderId} isCartModalOpen={isCartModalOpen} />
        </AppErrorBoundary>
      )}
      {data && (
        <h5>
          Page {data.currentPage} | <span>{data.data?.length} Orders</span>
        </h5>
      )}
      <TableActions premium={true} inputValues={inputValues} />
      <AppErrorBoundary>
        <FilterComponent
          handleSubmit={handleSubmit}
          filterValues={filteInputs}
          timeSlots={timeSlots}
          userAgents={userAgents}
          areas={areas}
          inputValues={inputValues}
          setInputValues={setInputValues}
          zones={zones}
        />
      </AppErrorBoundary>
      <PremiumQuickActionsDataTable params={inputValues} />
    </Fragment>
  );
};

export default PremiumOrdersQuickActionsPage;
